<template>
  <v-card class="qa-card" variant="flat">
    <v-card-title class="d-flex align-center py-3">
      <v-icon color="primary" size="18" class="mr-2">mdi-robot-outline</v-icon>
      <span class="text-subtitle-1">{{ $t('qaAssistRecord') }}</span>
      <v-spacer />
      <span class="text-caption text-medium-emphasis d-none d-sm-inline">สิ่งที่ผู้ช่วย AI เสนอให้เจ้าหน้าที่ระหว่างการสนทนา</span>
    </v-card-title>
    <v-divider />

    <v-row no-gutters>
      <!-- สรุปสาย — Q11: ของจริงจาก cc.QaCaseSummaries (ฉบับ AI + ฉบับที่ agent แก้แล้วส่งจริง) -->
      <v-col cols="12" md="6" class="pa-4">
        <div class="d-flex align-center mb-1">
          <v-icon size="17" color="indigo" class="mr-2">mdi-text-box-outline</v-icon>
          <span class="text-subtitle-2">สรุปการสนทนา</span>
          <v-chip v-if="hasSummaryData" size="x-small" color="success" variant="tonal" label class="ml-2">ข้อมูลจริง</v-chip>
        </div>
        <div class="text-caption text-medium-emphasis mb-3">
          ร่างอัตโนมัติจากบทสนทนา — ระบบส่งไปเติมในฟอร์มปิดงานของเจ้าหน้าที่ให้แก้ก่อนส่ง
        </div>

        <!-- ⚠️ เช็คถึงตัว summary ไม่ใช่แค่ assist — เคสอาจมีแต่ nextBestActions
             ถ้าเช็คแค่ assist จะได้การ์ดหัวข้อว่าง 5 แถวที่ดูเหมือนระบบพัง -->
        <v-card v-if="assist && assist.summary" variant="flat" class="pa-3 qa-card-sm qa-summary-card">
          <div class="d-flex align-center mb-2">
            <v-chip size="x-small" color="indigo" variant="tonal" label>ฉบับ AI แนะนำ</v-chip>
            <span v-if="summaryMeta.generatedAt" class="text-caption text-medium-emphasis ml-2">{{ summaryMeta.generatedAt }}</span>
          </div>
          <div v-for="row in summaryRows" :key="row.label" class="mb-3">
            <div class="text-caption text-medium-emphasis">{{ row.label }}</div>
            <div class="text-body-2">{{ row.value }}</div>
          </div>
        </v-card>
        <!-- สถานะว่างต้องบอก "ทำไม" — ว่างมีได้หลายสาเหตุที่คนละเรื่องกันโดยสิ้นเชิง -->
        <div v-else class="text-body-2 text-medium-emphasis">{{ summaryEmptyText }}</div>

        <!-- ฉบับที่ agent แก้แล้วกด submit จริง — นี่คือบันทึกของเคส ส่วนข้างบนเป็นแค่ร่าง -->
        <template v-if="agentSummary">
          <div class="d-flex align-center mb-2 mt-4">
            <v-chip size="x-small" color="success" variant="tonal" label>ฉบับที่เจ้าหน้าที่ส่งจริง</v-chip>
            <span v-if="summaryMeta.submittedAt" class="text-caption text-medium-emphasis ml-2">{{ summaryMeta.submittedAt }}</span>
          </div>
          <v-card variant="flat" class="pa-3 qa-card-sm qa-summary-card">
            <div class="text-body-2 qa-summary-prewrap">{{ agentSummary }}</div>
          </v-card>
        </template>
      </v-col>

      <!--
        คำตอบที่แนะนำ — ⚠️ ช่องนี้เป็น **ข้อมูลจริง** จากงานเบื้องหลัง (คนละงานกับสรุปด้านซ้าย
        ซึ่งเป็นของจริงเช่นกันแต่มาจาก Q11) · ใช้ QaRecommendPanel ตัวเดียวกับที่อื่น เพื่อให้มีที่เดียวที่รู้เรื่อง
        รอบ/สถานะ/การกดใช้-ไม่ใช้ ไม่ใช่ก๊อปมาร์กอัปมาไว้สองที่แล้วแก้ไม่ครบ
      -->
      <v-col cols="12" md="6" class="pa-4">
        <div class="d-flex align-center mb-2">
          <v-icon size="17" color="primary" class="mr-2">mdi-lightbulb-on-outline</v-icon>
          <span class="text-subtitle-2">คำตอบที่แนะนำ</span>
          <v-chip size="x-small" color="success" variant="tonal" label class="ml-2">ข้อมูลจริง</v-chip>
        </div>
        <!--
          ⚠️ ห้ามเอา questionCount กลับมาโชว์ตรงนี้ — ตัวนับเป็นของ**ลูกค้า 1 คน**
             (ทุกเคสรวมกัน) ส่วนหน้านี้เป็นของ**เคสใบเดียว** · เคสที่เพิ่งเปิดจะขึ้นว่า
             "ถามไปแล้ว 28 คำถาม" ทั้งที่ในเคสนี้เพิ่งถามข้อแรก อ่านแล้วเหมือนระบบ
             กลืนคำแนะนำไป (เจอจริง 20 ส.ค. 2026)
        -->
        <div v-if="recommend.isEnded" class="text-caption text-medium-emphasis mb-3">
          จบการสนทนาแล้ว
        </div>

        <QaRecommendPanel :data="recommend" embedded />

        <!-- ⚠️ ห่อด้วย v-if — prop assist เป็น null ได้ระหว่างที่เคสยังโหลดไม่เสร็จ -->
        <template v-if="assist && assist.nextBestActions">
        <div class="d-flex align-center mb-2 mt-4">
          <v-icon size="17" color="indigo" class="mr-2">mdi-compass-outline</v-icon>
          <span class="text-subtitle-2">Next best action</span>
          <v-chip size="x-small" variant="tonal" label class="ml-2">{{ assist.nextBestActions.length }} รายการ</v-chip>
          <!-- บอกว่ารายการถูกใส่กล่องเลื่อนแล้ว — ไม่งั้นคนอ่านจะคิดว่าเห็นครบทุกใบ -->
          <v-chip v-if="assist.nextBestActions.length > 3" size="x-small" variant="text" label class="ml-1" prepend-icon="mdi-gesture-swipe-vertical">
            เลื่อนดูได้
          </v-chip>
          <v-chip v-if="isLive" size="x-small" color="primary" variant="tonal" label class="ml-2">ข้อมูลจริง</v-chip>
        </div>

        <!-- ยังไม่มีคำแนะนำจริงสักใบ — ต้องอธิบายว่าทำไม ไม่ใช่ปล่อยว่างเฉย ๆ
             เพราะ "ว่าง" มีได้ 2 สาเหตุที่คนละเรื่องกันโดยสิ้นเชิง -->
        <v-alert v-if="isLive && assist.nextBestActions.length === 0" type="info" variant="tonal" density="compact" class="mb-2">
          <span class="text-body-2">
            ยังไม่มีคำแนะนำสำหรับเคสนี้ — เกิดได้ 2 กรณี: Salesforce ยังไม่ได้ส่งข้อมูลลูกค้ามาให้
            หรือบทสนทนายังไม่มีข้อความใหม่หลังจากได้รับข้อมูลนั้น
          </span>
        </v-alert>

        <!-- ⚠️ ห่อด้วยกล่อง scroll — เคสที่คุยกันยาวสะสม NBA ได้หลายสิบใบ (1 ใบ/รอบ ต่อทุก
             ครั้งที่ลูกค้าพิมพ์) · ปล่อยไหลยาวแล้วครึ่งซ้าย (สรุป) กับ transcript ด้านล่าง
             ถูกดันหายไปจากสายตา ทั้งที่เป็นสิ่งที่ทีม QA เปิดหน้านี้มาดู -->
        <div :class="{ 'qa-nba-scroll': assist.nextBestActions.length > 3 }">
        <v-card v-for="(nba, i) in assist.nextBestActions" :key="nba.nbaId || `nba-${i}`" variant="outlined" class="pa-3 mb-2 qa-card-sm">
          <div class="d-flex align-center mb-2">
            <v-chip :color="nbaColor(nba)" size="x-small" variant="tonal" label>{{ nba.category }}</v-chip>
            <span v-if="nba.timestamp" class="text-caption text-medium-emphasis ml-2">{{ nba.timestamp }}</span>
            <v-spacer />

            <!-- ⚠️ ข้อมูลจริงไม่มี `used` — Salesforce ไม่ได้บอกว่าเจ้าหน้าที่ใช้คำแนะนำหรือไม่
                 สิ่งที่รู้จริงคือ "กดไลค์ / unlike" ซึ่งเป็นคนละเรื่องกัน -->
            <template v-if="nba.used">
              <v-chip :color="nba.used === 'accepted' ? 'success' : 'grey'" size="x-small" variant="tonal" label>
                {{ nba.used === 'accepted' ? 'เจ้าหน้าที่ใช้' : 'ไม่ได้ใช้' }}
              </v-chip>
            </template>
            <template v-else>
              <v-chip
                :color="nba.rating === 'up' ? 'success' : nba.rating === 'down' ? 'error' : 'grey'"
                size="x-small"
                variant="tonal"
                label
                :prepend-icon="nba.rating === 'up' ? 'mdi-thumb-up-outline' : nba.rating === 'down' ? 'mdi-thumb-down-outline' : 'mdi-minus'"
              >
                {{ nba.rating === 'up' ? 'ไลค์' : nba.rating === 'down' ? 'ไม่ไลค์' : 'ยังไม่ให้ความเห็น' }}
              </v-chip>
            </template>
          </div>
          <div class="d-flex mb-1">
            <span class="qa-nba-label">สิ่งที่ควรทำ</span>
            <span class="text-body-2">{{ nba.action }}</span>
          </div>
          <div class="d-flex mb-1">
            <span class="qa-nba-label">คำพูด</span>
            <span class="text-body-2 font-italic">"{{ nba.say }}"</span>
          </div>
          <!-- คำแปลไทย — มีเฉพาะตอนที่ AI แนะนำคำพูดเป็นภาษาอื่น -->
          <div v-if="nba.sayTh" class="d-flex mb-1">
            <span class="qa-nba-label">แปลคำพูด</span>
            <span class="text-body-2 font-italic text-medium-emphasis">"{{ nba.sayTh }}"</span>
          </div>
          <div class="d-flex">
            <span class="qa-nba-label">เหตุผล</span>
            <span class="text-body-2 text-medium-emphasis">{{ nba.why }}</span>
          </div>
          <!-- เหตุผลตอน unlike — เป็นสิ่งเดียวที่บอกได้ว่าคำแนะนำพลาดตรงไหน -->
          <div v-if="nba.ratingReasons && nba.ratingReasons.length" class="d-flex mt-1">
            <span class="qa-nba-label">เหตุผลที่ไม่ไลค์</span>
            <span class="text-body-2 text-error">{{ nba.ratingReasons.join(' · ') }}</span>
          </div>
        </v-card>
        </div>
        </template>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
/**
 * บันทึกผู้ช่วย AI ของเคส
 *
 * ครึ่งซ้าย (สรุปการสนทนา) เป็น**ของจริง**แล้ว (Q11 — cc.QaCaseSummaries) และมี 2 ฉบับ
 * ที่เป็นคนละสิ่งกันโดยเจตนา ห้ามยุบเหลือก้อนเดียว:
 *   • "ฉบับ AI แนะนำ" — งานเบื้องหลังสรุปหลังเคสปิด เป็นร่างที่ระบบส่งไปเติมในฟอร์มปิดงาน
 *   • "ฉบับที่เจ้าหน้าที่ส่งจริง" — สิ่งที่ agent แก้แล้ว submit กลับมากับ agent-feedback
 *     = บันทึกจริงของเคส · ส่วนต่างระหว่างสองฉบับคือข้อมูลคุณภาพของตัว AI เอง
 *
 * ครึ่งขวามีของจริง 2 ชุดที่มาคนละทางและเป็นคนละฟีเจอร์กัน ห้ามเอามาปนกัน:
 *   • "คำตอบที่แนะนำ" — QA Recommend · ทุก N คำถามของลูกค้า · เขียนกลับได้ (ใช้/ไม่ใช้)
 *   • "Next best action" — Q6 · ทุกครั้งที่ลูกค้าพิมพ์ ตราบใดที่ยังมี customer context จาก
 *     Salesforce · ความเห็นของเจ้าหน้าที่คือไลค์/ไม่ไลค์ ไม่ใช่ใช้/ไม่ใช้
 */
import QaRecommendPanel from './QaRecommendPanel.vue';

export default {
  name: 'QaAssistPanel',
  components: { QaRecommendPanel },
  props: {
    assist: { type: Object, default: null },
    recommend: { type: Object, required: true },
  },
  computed: {
    /** ชุด NBA นี้มาจากฐานข้อมูลจริงหรือไม่ (ชั้น service เป็นคนติดธง isLiveNba ให้) */
    isLive() {
      return !!this.assist && this.assist.isLiveNba === true;
    },
    /** ก้อนสถานะจาก GET cases/{caseKey}/summary — null เมื่อโหลดไม่ได้/สิทธิ์ไม่พอ */
    callSummary() {
      return (this.assist && this.assist.callSummary) || null;
    },
    agentSummary() {
      return (this.callSummary && this.callSummary.agentSummary) || null;
    },
    hasSummaryData() {
      return !!((this.assist && this.assist.summary) || this.agentSummary);
    },
    summaryMeta() {
      const cs = this.callSummary || {};
      return { generatedAt: cs.generatedAt || null, submittedAt: cs.submittedAt || null };
    },
    summaryRows() {
      const s = (this.assist && this.assist.summary) || {};
      return [
        { label: 'เหตุผลที่ติดต่อ', value: s.reason },
        { label: 'ประเด็นสำคัญ', value: s.issues },
        { label: 'สิ่งที่ดำเนินการ', value: s.actions },
        { label: 'คำมั่นที่ให้ไว้', value: s.commitments },
        { label: 'สิ่งที่ต้องติดตาม', value: s.followUp },
        // ช่องที่โมเดลตอบ null ถูกตัดทิ้ง — แถวป้ายเปล่า 5 แถวอ่านเหมือนระบบพัง
      ].filter((row) => !!row.value);
    },
    /**
     * สถานะว่างของฝั่ง AI — "ว่าง" มีได้ 6 สาเหตุที่คนละเรื่องกัน ต้องเล่าให้ถูกอัน
     * (status มาจาก GET cases/{caseKey}/summary · ไม่มีก้อนเลย = โหลดไม่ได้/ไม่มีสิทธิ์)
     */
    summaryEmptyText() {
      const cs = this.callSummary;
      if (!cs) return 'ยังไม่มีสรุปของเคสนี้';
      const reasons = {
        'in-progress': 'จะสรุปให้อัตโนมัติหลังจบการสนทนา',
        pending: 'เคสปิดแล้ว — กำลังรอสรุปอัตโนมัติ (ปกติไม่เกิน 1 นาที)',
        disabled: 'งานสรุปอัตโนมัติปิดอยู่ (ค่าตั้ง section "summary" ใน cc.QaSettings)',
        failed: 'สรุปอัตโนมัติไม่สำเร็จ — ระบบเลิกลองแล้ว (นโยบายไม่ retry)',
      };
      if (cs.status === 'skipped') {
        const why = {
          'empty-transcript': 'ไม่มีบทสนทนาให้สรุป',
          'no-agent-lines': 'บอทจบการสนทนาเอง ไม่มีเจ้าหน้าที่ตอบ',
          'excluded-agent': 'บัญชีนี้ถูกตั้งค่าให้ข้าม (ExcludeAgentKeys)',
        };
        return `ข้ามการสรุป — ${why[cs.skipReason] || cs.skipReason || 'ไม่ทราบเหตุผล'}`;
      }
      return reasons[cs.status] || 'ยังไม่มีสรุปของเคสนี้';
    },
  },
  methods: {
    /**
     * ⚠️ รับทั้งใบ ไม่ใช่แค่ชื่อหมวด — ข้อมูลจริงส่ง typeId แบบ kebab-case มาด้วย
     *    ("cross-sell") ส่วนข้อมูลจำลองมีแต่ชื่อที่คนอ่าน ("Cross-sell")
     *    ถ้าเทียบด้วยชื่ออย่างเดียว การ์ดของจริงจะเป็นสีเทาหมดทุกใบ
     */
    nbaColor(nba) {
      const byId = { 'cross-sell': 'primary', retention: 'warning', 'remind-promotion': 'pink', compliance: 'indigo' };
      const byLabel = { 'Cross-sell': 'primary', Compliance: 'indigo', Retention: 'warning' };
      return byId[nba.typeId] || byLabel[nba.category] || 'grey';
    },
  },
};
</script>

<style scoped>
/* พื้นหลังเทาโดยไม่ให้ Vuetify ย้อมสีตัวอักษรตาม (variant="tonal" จะทำให้ข้อความจางจนอ่านยาก) */
.qa-summary-card {
  background: #fafbfc;
  border: 1px solid #eef0f2;
}
/* กล่อง scroll ของรายการ NBA — ความสูงคุมด้วย vh ไม่ใช่ px คงที่ เพื่อให้จอเตี้ย
   (โน้ตบุ๊ก 13") ไม่โดนบีบจนเห็นทีละใบครึ่ง และจอสูงก็ไม่เหลือช่องว่างเปล่า
   ⚠️ padding ขวาเผื่อไว้ให้ขอบการ์ดไม่ถูกแถบ scroll ทับ */
.qa-nba-scroll {
  max-height: 46vh;
  overflow-y: auto;
  overscroll-behavior: contain; /* เลื่อนจนสุดแล้วต้องไม่ลากทั้งหน้าต่อ */
  padding-right: 4px;
}
.qa-nba-scroll::-webkit-scrollbar {
  width: 6px;
}
.qa-nba-scroll::-webkit-scrollbar-thumb {
  background: #d7dbe0;
  border-radius: 3px;
}
.qa-nba-scroll::-webkit-scrollbar-thumb:hover {
  background: #c2c8ce;
}
.qa-summary-prewrap {
  white-space: pre-line; /* ฉบับ agent เป็นข้อความแบนที่มีขึ้นบรรทัด — ต้องเห็นตามที่พิมพ์ */
}
.qa-nba-label {
  min-width: 82px;
  font-size: 11px;
  color: #6b7280;
  padding-top: 2px;
  flex: none;
}
</style>
